import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Wand2, Check, Undo2, ChevronLeft, ChevronRight, Trash2, ArrowLeftRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

// Curated lists of common gendered first names
const MALE_NAMES = new Set([
  "james","john","robert","michael","david","william","richard","joseph","thomas","charles",
"christopher","daniel","dan","matthew","anthony","mark","donald","steven","steve","paul","andrew","andrew","joshua","josh","jim","james",
"kenneth","kevin","kelvin","brian","bryan","byron","george","timothy","ronald","edward","jason","jeffrey","jeffery","ryan",
  "jacob","glen","glenn","gary","nicholas","eric","jonathan","stephen","larry","justin","scott","brandon",
"benjamin","samuel","raymond","gregory","frank","patrick","jack","dennis","jerry","tyler",
  "jeremy","aaron","jose","adam","nathan","nathaniel","henry","douglas","peter","phillip","zachary","kyle","noah",
  "ken","harold","carl","wayne","arthur","gerald","roger","keith","lawrence","terry","sean",
"jessie","christian","austin","alfred","willie","albert","billy","bruce","clifton","ralph","eugene","russell",
  "bobby","mason","philip","louis","harry","vincent","martin","joe","joel","randy","roy","dale","leonard","nick","theodore",
  "travis","russell","howard","fred","frederick","earl","danny","leon","allen","alan","allan","marvin","darrell",
"lloyd","jerome","herman","melvin","claude","edwin","lester","cecil","clifford","arnold","clayton","craig",
"dewey","doyle","duane","elmer","floyd","homer","horacio","kurt","lyle","marshall","marquis","neal","neil","norman","oscar",
  "rex","rod","roland","ross","stuart","ted","todd","troy","vernon","victor","wade",
  "warren","walter","wesley","wilberto","willard","clyde","dean","desmond","don","doug","dwight","ernest","gene","glen","herbert",
  "gordon","grant","greg","ivan","karl","lee","leo","loren","marlin","morris","murray",
"otis","owen","perry","preston","rodney","ruben","spencer","stanley","terrence","virgil","wendell",
  "brent","brett","chad","cody","cole","colton","connor","derek","derrick","drew","dylan",
  "ethan","evan","garrett","hunter","ian","isaac","jake","jared","jordan","julius","kirk","landon","logan",
"luke","marcus","miles","mitchell","parker","riley","shane","tanner","trevor","tristan",
  "blake","caleb","calvin","cameron","carson","chase","cody","colby","conrad","dalton","dakota","dominic",
  "dustin","eli","elijah","gavin","grant","hayden","jackson","jayden","levi","liam",
  "mason","owen","wyatt","aidan","bentley","bryce","carter","easton","grayson","hudson",
  "jace","kai","max","oliver","ryder","sawyer","weston","xavier","zane","holland",
  "muhammad","ahmed","ali","omar","hassan","hussein","ibrahim","mohammed","mustafa","ahmad",
"raj","arjun","amir","sanjay","vikram","rahul","suresh","ravi","deepak","manoj",
  "carlos","juan","luis","mario","miguel","pedro","ramon","rafael","diego","sergio","alejandro",
  "andre","antoine","pierre","jean","jacques","claude","philippe","yves","marc","laurent",
  "hans","karl","fritz","heinrich","ernst","werner","hermann","rudolf","dieter","klaus",
  "dmitri","ivan","vladimir","sergei","nikolai","alexander","andrei","mikhail","boris","yuri",
]);

const FEMALE_NAMES = new Set([
  "mary","patricia","jennifer","linda","barbara","elizabeth","susan","jessica","sarah","karen",
  "lisa","nancy","betty","margaret","sandra","ashley","dorothy","kimberly","emily","donna",
  "michelle","carol","amanda","melissa","deborah","debbie","stephanie","rebecca","sharon","laura","laurie",
  "cynthia","kathleen","kathy","amy","angela","shirley","anna","anne","brenda","pamela","emma","nicole","helen",
  "samantha","katherine","christine","christie","geraldine","debra","rachel","carolyn","janet","catherine","maria","heather",
  "diane","melanie","ruth","julie","olivia","joyce","virginia","victoria","kelly","lauren","christina",
  "joan","evelyn","judith","megan","andrea","cheryl","hannah","jacqueline","martha","gloria",
  "teresa","ann","sara","madison","frances","kathryn","janice","jean","abigail","alice",
  "judy","sophia","grace","denise","amber","doris","marilyn","danielle","beverly","isabella",
  "theresa","diana","natalie","brittany","charlotte","marie","kayla","alexis","lori",
  "tammy","crystal","tiffany","robin","tracy","jill","dawn","shannon","stacy","sherry","sherrie",
"tina","phyllis","norma","paula","irene","jane","janie","lois","carmen","bonnie","elsie","gail",
  "lynda","dolores","delores","angelica","audrey","erin","annette","roseylyn",
  "bernice","esther","cathy","pattie","ronda","rhonda","marguerite","tamara","cathleen","marjorie","katy","cristina","edith",
  "hazel","irma","lucille","mildred","nellie","rosa","vivian","wanda","wilma","yvonne",
  "adrienne","allison","belinda","bethany","bettie","bridget","candice","carla","caroll","cassandra","colleen","connie",
  "darlene","deanna","eileen","elaine","erica","faye","gina","gwen","holly","ingrid",
  "jeanette","joanne","jolene","kara","kristen","kristin","leah","leigh","lorraine","lydia",
  "marcia","maureen","meredith","miranda","molly","monique","nadia","nina","noelle","paige",
  "priscilla","renee","rhonda","roberta","roxanne","sabrina","sandy","selena","sheila","sonya",
  "stella","sue","susie","sylvia","tara","traci","trudy","valerie","vanessa","vera",
  "wendy","yolanda","zoey","addison","aria","aubrey","aurora","avery","bailey","brooke",
  "chloe","claire","daisy","elena","ella","eva","faith","gabriella","gianna","hailey",
  "harper","hazel","jade","jasmine","julia","kaitlyn","kaylee","kennedy","layla","leah",
  "lillian","lucy","lydia","mackenzie","makayla","maya","mia","naomi","natasha","nora",
  "penelope","peyton","piper","quinn","reagan","riley","ruby","rylee","savannah","scarlett",
  "serenity","skylar","stella","taylor","violet","willow","zoe",
  "fatima","aisha","mariam","yasmin","layla","noor","hana","sara","dina","rania",
  "priya","anita","sunita","deepa","kavita","lakshmi","meena","nita","pooja","rekha",
  "ana","carmen","elena","lucia","rosa","sofia","valentina","alejandra","gabriela","isabel",
  "marie","sophie","claire","colette","monique","brigitte","isabelle","sylvie","nathalie",
  "anna","greta","heidi","ingrid","katrin","petra","ursula","helga","elke","monika",
  "natasha","olga","tatiana","svetlana","yelena","irina","marina","galina","lyudmila",
]);

interface PrefixRecord {
  id: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  prefix: string | null;
  suggested_prefix: string;
  explicitly_none: boolean; // user chose "none" — commit as empty string
  confidence: "high" | "medium" | "low";
  reason: string;
}

function detectPrefix(firstName: string | null, businessName: string | null): { prefix: string; confidence: "high" | "medium" | "low"; reason: string } {
  if (!firstName || firstName.trim() === "") {
    if (businessName && businessName.trim() !== "") {
      return { prefix: "", confidence: "high", reason: "Business entity — no prefix" };
    }
    return { prefix: "", confidence: "low", reason: "No first name" };
  }

  const raw = firstName.trim();
  const name = raw.toLowerCase();

  // Check for couple patterns: "John & Mary", "John and Mary", "John/Mary"
  // Require whitespace around "and" so we don't match it inside names like "Brandon" or "Sandra"
  // Do NOT auto-assign "Mr. and Mrs." — flag for manual review instead
  const coupleMatch = name.match(/^(\w+)(?:\s*[&\/]\s*|\s+and\s+)(\w+)$/i);
  if (coupleMatch) {
    const [, name1, name2] = coupleMatch;
    return { prefix: "", confidence: "medium", reason: `Two names detected ("${name1}" & "${name2}") — review manually` };
  }

  // Single name lookup
  const isMale = MALE_NAMES.has(name);
  const isFemale = FEMALE_NAMES.has(name);

  if (isMale && !isFemale) return { prefix: "Mr.", confidence: "high", reason: `"${raw}" is a recognized male name` };
  if (isFemale && !isMale) return { prefix: "Ms.", confidence: "high", reason: `"${raw}" is a recognized female name` };
  if (isMale && isFemale) return { prefix: "", confidence: "low", reason: `"${raw}" is ambiguous (both male/female)` };

  return { prefix: "", confidence: "low", reason: `"${raw}" not found in name lists` };
}

const PAGE_SIZE = 50;

export function PrefixAutoAssign({ onComplete }: { onComplete?: () => void }) {
  const [records, setRecords] = useState<PrefixRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [filterConfidence, setFilterConfidence] = useState<string>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const { toast } = useToast();

  // Filter state
  const [filterCounty, setFilterCounty] = useState<string>("__all__");
  const [filterState, setFilterState] = useState<string>("__all__");
  const [filterRound, setFilterRound] = useState<string>("__all__");
  const [counties, setCounties] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);

  // Load available counties & states on mount (paginate to bypass 1k limit)
  const loadFilterOptions = async () => {
    const fetchAll = async (col: "county" | "origin_state") => {
      const all: string[] = [];
      const pageSize = 1000;
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("mailing_records")
          .select(col)
          .is("prefix", null)
          .eq("mailing_status", "active")
          .not(col, "is", null)
          .range(from, from + pageSize - 1);
        if (error || !data || data.length === 0) break;
        all.push(...data.map((r: any) => r[col] as string));
        if (data.length < pageSize) break;
      }
      return all;
    };
    const [countyVals, stateVals] = await Promise.all([fetchAll("county"), fetchAll("origin_state")]);
    setCounties([...new Set(countyVals)].sort());
    setStates([...new Set(stateVals)].sort());
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadRecords = async () => {
    setLoading(true);
    try {
      // Build base query filters
      const applyFilters = (q: any) => {
        q = q.is("prefix", null).eq("mailing_status", "active");
        if (filterCounty !== "__all__") q = q.eq("county", filterCounty);
        if (filterState !== "__all__") q = q.eq("origin_state", filterState);
        if (filterRound !== "__all__") q = q.eq("times_mailed", parseInt(filterRound));
        return q;
      };

      // Count
      const countQuery = applyFilters(supabase.from("mailing_records").select("*", { count: "exact", head: true }));
      const { count } = await countQuery;
      setTotalCount(count || 0);

      // Fetch records
      let dataQuery = applyFilters(
        supabase.from("mailing_records").select("id, first_name, last_name, business_name, prefix, county, origin_state, times_mailed")
      );
      dataQuery = dataQuery.order("last_name").limit(5000);
      const { data, error } = await dataQuery;

      if (error) throw error;

      const processed: PrefixRecord[] = (data || []).map((r) => {
        const detection = detectPrefix(r.first_name, r.business_name);
        return {
          id: r.id,
          first_name: r.first_name,
          last_name: r.last_name,
          business_name: r.business_name,
          prefix: r.prefix,
          suggested_prefix: detection.prefix,
          explicitly_none: false,
          confidence: detection.confidence,
          reason: detection.reason,
        };
      });

      setRecords(processed);
      setPage(0);
    } catch (err) {
      console.error(err);
      toast({ title: "Error loading records", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePrefixChange = (id: string, newPrefix: string, isNone: boolean) => {
    setRecords((prev) =>
      prev.map((r) => (r.id === id ? { ...r, suggested_prefix: newPrefix, explicitly_none: isNone } : r))
    );
  };

  const startEditing = (record: PrefixRecord) => {
    setEditingId(record.id);
    setEditFirstName(record.first_name || "");
    setEditLastName(record.last_name || "");
  };

  const saveNameEdit = async (id: string) => {
    try {
      const { error } = await supabase
        .from("mailing_records")
        .update({ first_name: editFirstName.trim() || null, last_name: editLastName.trim() || null })
        .eq("id", id);
      if (error) throw error;

      // Re-detect prefix with new name
      const detection = detectPrefix(editFirstName.trim() || null, records.find(r => r.id === id)?.business_name || null);
      setRecords((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, first_name: editFirstName.trim() || null, last_name: editLastName.trim() || null, suggested_prefix: detection.prefix, explicitly_none: false, confidence: detection.confidence, reason: detection.reason }
            : r
        )
      );
      setEditingId(null);
      toast({ title: "Name updated" });
    } catch (err) {
      console.error(err);
      toast({ title: "Error updating name", variant: "destructive" });
    }
  };

  const deleteRecord = async (id: string) => {
    setDeleting(id);
    try {
      const { error } = await supabase
        .from("mailing_records")
        .delete()
        .eq("id", id);
      if (error) throw error;

      setRecords((prev) => prev.filter((r) => r.id !== id));
      setTotalCount((c) => c - 1);
      toast({ title: "Record deleted" });
      onComplete?.();
    } catch (err) {
      console.error(err);
      toast({ title: "Error deleting record", variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  };

  const swapNames = async (record: PrefixRecord) => {
    const newFirst = record.last_name || "";
    const newLast = record.first_name || "";
    try {
      const { error } = await supabase
        .from("mailing_records")
        .update({ first_name: newFirst.trim() || null, last_name: newLast.trim() || null })
        .eq("id", record.id);
      if (error) throw error;
      const detection = detectPrefix(newFirst.trim() || null, record.business_name || null);
      setRecords((prev) =>
        prev.map((r) =>
          r.id === record.id
            ? { ...r, first_name: newFirst.trim() || null, last_name: newLast.trim() || null, suggested_prefix: detection.prefix, confidence: detection.confidence, reason: detection.reason, explicitly_none: false }
            : r
        )
      );
      toast({ title: "Names swapped" });
    } catch (err) {
      console.error(err);
      toast({ title: "Error swapping names", variant: "destructive" });
    }
  };

  const commitPrefixes = async () => {
    const toCommit = records.filter((r) => r.suggested_prefix !== "" || r.explicitly_none);
    if (toCommit.length === 0) {
      toast({ title: "Nothing to commit", description: "No prefixes assigned" });
      return;
    }

    setSaving(true);
    try {
      let successCount = 0;
      const batchSize = 50;
      for (let i = 0; i < toCommit.length; i += batchSize) {
        const batch = toCommit.slice(i, i + batchSize);
        const promises = batch.map((r) =>
          supabase
            .from("mailing_records")
            .update({ prefix: r.explicitly_none ? "" : r.suggested_prefix })
            .eq("id", r.id)
        );
        const results = await Promise.all(promises);
        successCount += results.filter((r) => !r.error).length;
      }

      toast({
        title: "Prefixes Saved!",
        description: `Updated ${successCount} of ${toCommit.length} records`,
      });

      // Remove committed records from view
      setRecords((prev) => prev.filter((r) => r.suggested_prefix === "" && !r.explicitly_none));
      onComplete?.();
    } catch (err) {
      console.error(err);
      toast({ title: "Error saving prefixes", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const filtered = filterConfidence === "all"
    ? records
    : records.filter((r) => r.confidence === filterConfidence);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRecords = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const stats = {
    high: records.filter((r) => r.confidence === "high").length,
    medium: records.filter((r) => r.confidence === "medium").length,
    low: records.filter((r) => r.confidence === "low").length,
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Prefix Auto-Assign
            </CardTitle>
            <CardDescription>
              Auto-detect Mr./Ms. from first names. Filter by county, state, or mailing round to scope your scan.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {records.length > 0 && (
              <>
                <Button variant="outline" onClick={() => { loadFilterOptions(); loadRecords(); }} disabled={loading}>
                  <Undo2 className="mr-2 h-4 w-4" /> Re-scan
                </Button>
                <Button onClick={commitPrefixes} disabled={saving}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                  Commit {records.filter((r) => r.suggested_prefix !== "" || r.explicitly_none).length} Prefixes
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filter controls — always visible */}
        <div className="flex items-end gap-3 flex-wrap mt-4 pt-4 border-t">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">County</label>
            <Select value={filterCounty} onValueChange={setFilterCounty}>
              <SelectTrigger className="h-8 w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Counties</SelectItem>
                {counties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">State</label>
            <Select value={filterState} onValueChange={setFilterState}>
              <SelectTrigger className="h-8 w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All States</SelectItem>
                {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Mailing Round</label>
            <Select value={filterRound} onValueChange={setFilterRound}>
              <SelectTrigger className="h-8 w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Rounds</SelectItem>
                <SelectItem value="0">Never Mailed (0)</SelectItem>
                <SelectItem value="1">Round 1</SelectItem>
                <SelectItem value="2">Round 2</SelectItem>
                <SelectItem value="3">Round 3+</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => { loadFilterOptions(); loadRecords(); }} disabled={loading} className="h-8">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
            Scan Records
          </Button>
        </div>
      </CardHeader>

      {records.length > 0 && (
        <CardContent className="space-y-4">
          {/* Stats bar */}
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm text-muted-foreground">{totalCount} records missing prefix</span>
            <Badge
              variant={filterConfidence === "all" ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => { setFilterConfidence("all"); setPage(0); }}
            >
              All ({records.length})
            </Badge>
            <Badge
              variant={filterConfidence === "high" ? "default" : "outline"}
              className="cursor-pointer bg-green-500/10 text-green-700 border-green-300 hover:bg-green-500/20"
              onClick={() => { setFilterConfidence("high"); setPage(0); }}
            >
              ✓ High ({stats.high})
            </Badge>
            <Badge
              variant={filterConfidence === "medium" ? "default" : "outline"}
              className="cursor-pointer bg-yellow-500/10 text-yellow-700 border-yellow-300 hover:bg-yellow-500/20"
              onClick={() => { setFilterConfidence("medium"); setPage(0); }}
            >
              ~ Medium ({stats.medium})
            </Badge>
            <Badge
              variant={filterConfidence === "low" ? "default" : "outline"}
              className="cursor-pointer bg-red-500/10 text-red-700 border-red-300 hover:bg-red-500/20"
              onClick={() => { setFilterConfidence("low"); setPage(0); }}
            >
              ? Low ({stats.low})
            </Badge>
          </div>

          {/* Table */}
          <div className="rounded-md border max-h-[500px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Prefix</TableHead>
                  <TableHead>First Name</TableHead>
                  <TableHead>Last Name</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRecords.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Select
                        value={r.explicitly_none ? "__none__" : r.suggested_prefix || "__unset__"}
                        onValueChange={(v) => {
                          if (v === "__none__") handlePrefixChange(r.id, "", true);
                          else handlePrefixChange(r.id, v, false);
                        }}
                      >
                        <SelectTrigger className="h-8 w-[130px]">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset__" disabled className="text-muted-foreground">Select...</SelectItem>
                          <SelectItem value="Mr.">Mr.</SelectItem>
                          <SelectItem value="Mrs.">Mrs.</SelectItem>
                          <SelectItem value="Mr. and Mrs.">Mr. and Mrs.</SelectItem>
                          <SelectItem value="Ms.">Ms.</SelectItem>
                          <SelectItem value="Dr.">Dr.</SelectItem>
                          <SelectItem value="__none__">— None (skip) —</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {editingId === r.id ? (
                        <Input
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          className="h-8 w-[120px]"
                          autoFocus
                        />
                      ) : (
                        <span
                          className="font-medium cursor-pointer hover:underline"
                          onClick={() => startEditing(r)}
                          title="Click to edit"
                        >
                          {r.first_name || "—"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === r.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editLastName}
                            onChange={(e) => setEditLastName(e.target.value)}
                            className="h-8 w-[120px]"
                          />
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => saveNameEdit(r.id)}>
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingId(null)}>
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span
                            className="cursor-pointer hover:underline"
                            onClick={() => startEditing(r)}
                            title="Click to edit"
                          >
                            {r.last_name || "—"}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                            onClick={() => swapNames(r)}
                            title="Swap first & last name"
                          >
                            <ArrowLeftRight className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.business_name || ""}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          r.confidence === "high"
                            ? "bg-green-500/10 text-green-700 border-green-300"
                            : r.confidence === "medium"
                            ? "bg-yellow-500/10 text-yellow-700 border-yellow-300"
                            : "bg-red-500/10 text-red-700 border-red-300"
                        }
                      >
                        {r.confidence}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {r.reason}
                    </TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            disabled={deleting === r.id}
                          >
                            {deleting === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete <strong>{r.first_name} {r.last_name}</strong> from your mailing records.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteRecord(r.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages} ({filtered.length} records)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
